import {tokens, networks} from '@web3icons/common';

import {HTTPError} from './fetch/base';
import {Currency} from '@/types';
import type {Coin} from '@/types/api/prices';

const INITIAL_PAGE_LOAD_TIMESTAMP = new Date();
const MIN_PRICE_IRT_DECIMAL = Number(
  process.env.NEXT_PUBLIC_MIN_PRICE_IRT_DECIMAL
);

const enLocale = new Intl.Locale('en-US');
const faLocale = new Intl.Locale('fa-IR');
const roundingMode: Intl.NumberFormatOptions['roundingMode'] = 'ceil';

const formatters = {
  [Currency.USDT]: new Intl.NumberFormat(enLocale, {
    maximumFractionDigits: 18,
    roundingMode
  }),
  [Currency.IRT]: new Intl.NumberFormat(enLocale, {
    maximumFractionDigits: 0,
    roundingMode
  }),
  irtWithDecimal: new Intl.NumberFormat(enLocale, {
    maximumFractionDigits: 2,
    roundingMode
  }),
  percent: new Intl.NumberFormat(faLocale, {
    maximumFractionDigits: 2,
    style: 'percent',
    signDisplay: 'exceptZero',
    roundingMode
  })
};

/**
 * Temporally adds a class or classes to given element or elements.
 * @param time - In milliseconds
 * @param callback - Runs after removing the classes.
 * @returns A callback function to cancel the process.
 */
export function temporalStyle<T extends HTMLElement | null>(
  element: T | React.RefObject<T> | (T | React.RefObject<T>)[],
  className: string | string[],
  time: number,
  callback?: () => void
) {
  const elements = Array.isArray(element)
    ? element.map(el => (el instanceof HTMLElement ? el : el?.current))
    : [element instanceof HTMLElement ? element : element?.current];

  const classNames: string[] =
    typeof className === 'string' ? [className] : className;

  for (const element of elements) {
    element?.classList.add(...classNames);
  }

  let removed = false;
  const timerId = window.setTimeout(() => {
    for (const element of elements) {
      element?.classList.remove(...classNames);
    }
    removed = true;
    callback?.();
  }, time);

  return () => {
    if (!removed) {
      window.clearTimeout(timerId);
      for (const element of elements) {
        element?.classList.remove(...classNames);
      }
    }
  };
}

/** Behaves like `Number().toFixed()` but also strips the trailing decimal zeros. */
export function toFixed(
  value: number | string | undefined,
  fractionDigits?: number
) {
  value = Number(value);
  const formatted = new Intl.NumberFormat(enLocale, {
    maximumFractionDigits:
      fractionDigits ??
      formatters[Currency.USDT].resolvedOptions().maximumFractionDigits,
    useGrouping: false,
    roundingMode
  }).format(value);

  return formatted === '∞' ? value : Number(formatted);
}

/**
 * Returns the acceptable length of the decimal
 * digits for the given IRT currency value.
 */
export function getCurrencyDecimalIRT(value: number | string) {
  value = Number(value);
  return formatters[
    value < 1
      ? Currency.USDT
      : value < MIN_PRICE_IRT_DECIMAL
      ? 'irtWithDecimal'
      : Currency.IRT
  ].resolvedOptions().maximumFractionDigits!;
}

/**
 * Formats the value for the given currency value.
 *
 * For formatting the token's amount and token's price based on USDT, the
 * {@link fractionDigits} parameter should set to the {@link Prices.decimal}
 * value and for formatting the token's price based on IRT,the {@link currency}
 * parameter should set to the {@link Currency.IRT}.
 *
 * @param currency - The currency that formatting is based on.
 * If set to the {@link Currency.USDT} or not specified, format the
 * value with all the decimal digits only if the {@link fractionDigits}
 * parameter is also not specified.
 * If set to the {@link Currency.IRT}, the specified formatter will be
 * adjusted for values smaller than {@link MIN_PRICE_IRT_DECIMAL}.
 *
 * @param fractionDigits - The maximum digits after the decimal point.
 * Only takes effect when the {@link currency} parameter is set to the
 * {@link Currency.USDT}.
 */
export function formatCurrency(
  value: number | string,
  currency?: Currency,
  fractionDigits?: number
) {
  value = Number(value);
  let formatter: Exclude<keyof typeof formatters, 'percent'> =
    currency ?? Currency.USDT;

  if (currency === Currency.USDT) {
    if (fractionDigits !== undefined)
      return new Intl.NumberFormat(enLocale, {
        maximumFractionDigits: fractionDigits,
        roundingMode
      }).format(value);
  } else if (currency === Currency.IRT) {
    if (value < 1) {
      formatter = Currency.USDT;
    } else if (value < MIN_PRICE_IRT_DECIMAL) {
      formatter = 'irtWithDecimal';
    }
  }

  return formatters[formatter].format(value);
}

/** Reverts the given formatted currency value to the number form. */
export function deFormatCurrency(value: number | string, currency: Currency) {
  return parseFloat(
    String(value).replaceAll(
      new RegExp(
        `\\${formatCurrency(1e3, currency).replace(/\p{Number}/gu, '')}`,
        'g'
      ),
      ''
    )
  );
}

/** Formats the given percentage value. */
export function formatPercent(
  percent: number | string,
  formatterOptions?: Intl.NumberFormatOptions
) {
  return (
    formatterOptions
      ? new Intl.NumberFormat(faLocale, {style: 'percent', ...formatterOptions})
      : formatters.percent
  )
    .format(Number(percent))
    .replace('٫', '.');
}

/** Formats the given date value to Iran standard date format. */
export function formatDate(date: string | Date) {
  return new Date(date).toLocaleDateString(faLocale);
}

/** Formats the given date value to US standard date format. */
export function formatDateUS(date: string | Date) {
  return new Date(date).toLocaleDateString(enLocale);
}

/** Formats the given date value to Iran standard date-time format. */
export function formatDateTime(date: string | Date) {
  return new Date(date).toLocaleString(faLocale);
}

/** Formats the given date value to US standard date-time format. */
export function formatDateTimeUS(date: string | Date) {
  return new Date(date).toLocaleString(enLocale);
}

/** Formats then given date value to a human-readable relative time format. */
export function formatRelativeDate(date: string | Date) {
  const _date = new Date(date);
  const now = new Date();
  const difference = Math.floor((now.getTime() - _date.getTime()) / 1e3);
  const isNegative = difference >= 0;
  const suffix = isNegative ? 'قبل' : 'بعد';
  const absoluteDifference = Math.abs(difference);

  if (absoluteDifference < 60) {
    return `${absoluteDifference} ثانیه ${suffix}`;
  } else if (absoluteDifference < 3600) {
    return `${Math.floor(absoluteDifference / 60)} دقیقه ${suffix}`;
  } else if (absoluteDifference < 86400) {
    return `${Math.floor(absoluteDifference / 3600)} ساعت ${suffix}`;
  } else if (absoluteDifference < 172800) {
    return isNegative ? 'دیروز' : 'فردا';
  } else {
    return formatDateTime(_date);
  }
}

/** Formats then given PAN identifier value. */
export function formatPANId(panId: number | string) {
  return String(panId)
    .replace(/(.{4})/g, '$1-')
    .replace(/-$/, '');
}

/** Converts a given size in bytes into a human-readable format with appropriate units. */
export function formatSize(
  value: number,
  isBinaryPrefix = false,
  separator = ''
) {
  if (value < 0) {
    throw new Error('Value must be a non-negative number.');
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const factor = isBinaryPrefix ? 1024 : 1e3;

  let unitIndex = 0;
  while (value >= factor && unitIndex < units.length - 1) {
    value /= factor;
    unitIndex++;
  }

  return `${toFixed(value)}${separator}${units[unitIndex]}`;
}

/** Converts a given amount in IRT into a human-readable format with appropriate units. */
export function formatCurrencyUintIRT(value: number, separator = ' ') {
  if (value < 0) {
    throw new Error('Value must be a non-negative number.');
  }

  const units = ['', 'هزار', 'میلیون', 'میلیارد'];
  const factor = 1e3;

  let unitIndex = 0;
  while (value >= factor && unitIndex < units.length - 1) {
    value /= factor;
    unitIndex++;
  }

  return `${toFixed(value)}${separator}${units[unitIndex]} تومان`;
}

/**
 * Returns the normalized Iranian phone number that
 * starts with zero and has stripped country code.
 */
export function normalizePhone(value: string | number) {
  const matches = Array.from(
    String(value).matchAll(/^((\+98)|0)?(9[\d]{9})$/g)
  );
  return matches.length ? `0${matches[0][3]}` : '';
}

/** Whether the given value is a valid email address. */
export function isValidEmail(value: string) {
  return /^[\w.+-]+@[a-z0-9]+\.[a-z]{2,10}$/i.test(value);
}

/** Whether the given value is a valid Iranian phone number. */
export function isValidPhone(value: string | number) {
  return !!normalizePhone(value);
}

/** Whether the given value is a valid password. */
export function isValidPassword(value: string | number) {
  value = String(value);
  if (value.length >= 8 && value.length <= 32) {
    return !/\s/.test(value);
  }

  return false;
}

/** Whether the given value is a valid Iranian national ID. */
export function isValidNationalId(value: string | number) {
  value = String(value);
  if (!/^\d{10}$/.test(value)) {
    return false;
  }

  const digits = value.split('').map(Number);
  const controlDigit = digits[9];
  const sum = digits
    .slice(0, 9)
    .reduce((acc, digit, index) => acc + digit * (10 - index), 0);

  const remainder = sum % 11;
  return (
    (remainder < 2 && controlDigit === remainder) ||
    (remainder >= 2 && controlDigit === 11 - remainder)
  );
}

/** Hides the default placeholder when the image element fails to load. */
export function hideImagePlaceholder<T extends HTMLImageElement>(
  event: React.SyntheticEvent<T, Event>
) {
  (event.target as T).src =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E";
}

/**
 * Prevents the default action of an event.
 * @param callback - Runs after preventing the default action.
 */
export function preventDefault(
  event: React.SyntheticEvent<HTMLElement | null, Event>,
  callback?: Function
) {
  event.preventDefault();
  callback?.();
}

/**
 * Prevents the default action of an event when
 * triggered outside the specified element or elements.
 */
export function excludedPreventDefault(
  event: React.SyntheticEvent<HTMLElement | null, Event>,
  element: (HTMLElement | null) | React.RefObject<HTMLElement | null>
) {
  const _element = element instanceof HTMLElement ? element : element?.current;
  if (_element && !_element.contains(event.target as Element))
    event.preventDefault();
}

/** Returns the computed value of the specified CSS property for the given element. */
export function getComputedCSSProperty(variable: string, element?: Element) {
  return window
    .getComputedStyle(element || document.documentElement)
    .getPropertyValue(variable);
}

/**
 * Scrolls to the corresponding element for the given element or hashtag.
 *
 * @param hash - Element's ID. If not provided, hashtag part of the
 * current page URL will be considered instead.
 * @param rewriteUrl - Whether to rewrite the hashtag on the URL.
 */
export function scrollInto(
  element?: React.RefObject<HTMLElement | null> | HTMLElement,
  hash?: string,
  options?: ScrollIntoViewOptions,
  rewriteUrl = true
) {
  options = {
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest',
    ...options
  };
  hash = hash || window.location.hash;

  const _element =
    element instanceof HTMLElement
      ? element
      : element?.current ||
        (hash ? document.getElementById(hash.slice(1)) : null);

  if (_element) {
    if (rewriteUrl && hash)
      window.history.replaceState(window.history.state, '', hash);
    _element.scrollIntoView(options);
  }
}

/**
 * Returns the timestamp of the initial page load,
 * either as a `number` (epoch time) or a `Date` object.
 */
export function getInitialPageLoadTimestamp(epoch: false): Date;
export function getInitialPageLoadTimestamp(epoch: true): number;
export function getInitialPageLoadTimestamp(epoch?: boolean) {
  return epoch
    ? INITIAL_PAGE_LOAD_TIMESTAMP.getTime()
    : INITIAL_PAGE_LOAD_TIMESTAMP;
}

/**
 * Throws the given error if it's not instance of {@link HTTPError}.
 *
 * @param log - Log the error if it's instance of {@link HTTPError}.
 *
 * This function should be used when React errors should not be silenced.
 * @see https://nextjs.org/docs/messages/ppr-caught-error
 */
export function shouldThrowError(error: any, log = true) {
  if (!(error instanceof HTTPError)) {
    throw error;
  } else if (log) {
    console.error(error);
  }

  return false;
}

/**
 * Returns the corresponding coin's network
 * full name for the given network's abbreviation.
 */
export function coinNetworkIdToName(id: Coin['id']) {
  const token = tokens.find(token => token.symbol === id);
  if (token) {
    let shortName;
    const name = token.name.toLowerCase();
    const networkName = networks.find(
      network =>
        network.name === name ||
        network.id?.toLowerCase() === name ||
        network.name?.toLowerCase() === name ||
        (shortName = network.shortName?.toLowerCase()) === name ||
        shortName?.startsWith(name)
    )?.name;

    if (networkName) {
      return networkName;
    }
  }

  // Fallback names for whenever the network name could
  // not be determined from the `@web3icons/react` package
  if (id === 'bsc')
    return networks.find(network => network.shortName?.toLowerCase() === id)
      ?.name;
  else if (id === 'ton') {
    return 'The Open Network';
  }
}
